import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import './index.css'
import { AuthProvider } from './contexts/AuthContext.tsx'
import App from './App.tsx'
import LandingPage from './pages/LandingPage.tsx'
import PricingPage from './pages/PricingPage.tsx'
import FeaturesPage from './pages/FeaturesPage.tsx'
import HowItWorksPage from './pages/HowItWorksPage.tsx'
import PrivacyPolicyPage from './pages/marketing/PrivacyPolicyPage.tsx'
import AuthRoutes from './pages/auth/AuthRoutes.tsx'
import SigningEntryPage from './pages/signing/SigningEntryPage.tsx'
import SigningWorkspacePage from './pages/signing/SigningWorkspacePage.tsx'
import SigningCompletePage from './pages/signing/SigningCompletePage.tsx'
import SigningErrorPage from './pages/signing/SigningErrorPage.tsx'
import SharedDocumentPage from './pages/shared/SharedDocumentPage.tsx'
import SharedInvitePage from './pages/shared/SharedInvitePage.tsx'
import AdminLayout from './pages/admin/AdminLayout.tsx'
import DashboardPage from './pages/admin/DashboardPage.tsx'
import UsersPage from './pages/admin/UsersPage.tsx'
import UserDetailPage from './pages/admin/UserDetailPage.tsx'
import RevenuePage from './pages/admin/RevenuePage.tsx'
import UsagePage from './pages/admin/UsagePage.tsx'
import DocumentsPage from './pages/admin/DocumentsPage.tsx'
import IntegrationsPage from './pages/admin/IntegrationsPage.tsx'
import ESignPage from './pages/admin/ESignPage.tsx'
import SettingsPage from './pages/admin/SettingsPage.tsx'
import ErrorBoundary from './components/ErrorBoundary.tsx'
import { applyTheme, subscribeToSystemTheme } from './lib/theme.ts'
import { applyChatFont } from './lib/chatFont.ts'

// Polyfill crypto.randomUUID for older browsers (Safari < 15.4, etc.)
if (typeof crypto !== 'undefined' && !crypto.randomUUID) {
  (crypto as Crypto).randomUUID = () => {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 1
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}` as `${string}-${string}-${string}-${string}-${string}`;
  };
}

// Build version marker — Vite injects the actual deployed commit SHA
// + build timestamp at build time (see vite.config.ts `define`). The
// previous hardcoded string ("2026-04-30T01-green-markers") never
// changed across deploys and led to hours of confusion debugging code
// that was already running. If the values below ever look stale on
// prod, the bundle is genuinely cached — not the marker lying.
console.log(`[Corner] Build: ${__CORNER_COMMIT_SHA__} @ ${__CORNER_BUILD_TIME__}`);

// Global error logging — catches module initialization errors that React error boundaries cannot
window.addEventListener('error', (event) => {
  console.error('[GlobalErrorHandler] Uncaught error:', event.message);
  console.error('[GlobalErrorHandler] Source:', event.filename, 'line:', event.lineno, 'col:', event.colno);
  if (event.error?.stack) {
    console.error('[GlobalErrorHandler] Stack:', event.error.stack);
  }
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('[GlobalErrorHandler] Unhandled promise rejection:', event.reason);
});

applyTheme()
applyChatFont()
subscribeToSystemTheme(() => applyTheme())

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/auth/*" element={<AuthRoutes />} />
        <Route path="/sign/:token" element={<SigningEntryPage />} />
        <Route path="/sign/:token/workspace" element={<SigningWorkspacePage />} />
        <Route path="/sign/:token/complete" element={<SigningCompletePage />} />
        <Route path="/sign/:token/error" element={<SigningErrorPage />} />
        <Route path="/app/shared/invite/:conversationId" element={<SharedInvitePage />} />
        <Route path="/app/shared/:token" element={<SharedDocumentPage />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="users/:userId" element={<UserDetailPage />} />
          <Route path="revenue" element={<RevenuePage />} />
          <Route path="usage" element={<UsagePage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="integrations" element={<IntegrationsPage />} />
          <Route path="esign" element={<ESignPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>
        <Route path="/app/*" element={<ErrorBoundary name="App"><App /></ErrorBoundary>} />
      </Routes>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
)
